import { connect } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { track, useTracking } from 'lib/analytics'
import {
  NewListIcon,
  CopyListIcon,
} from 'baby-ui/foundation/BabylistIcons/BabylistIcons'
import Image from 'components/image'
import { LinkButton } from 'components/buttons'
import { registryPath } from 'lib/urls'
// @ts-ignore
import { Route, Routes } from 'react-router-dom-v5-compat'
// @ts-ignore
import pluralize from 'pluralize'
import { RegistryProfileIcon } from 'components/icons'
import { getRegistry, getUser } from '../../reducers'
import { fetchTemplates } from '../../api'
import Slide from '../../components/slide/slide'
import { ButtonGroup, ButtonRect } from '../../components/buttons/buttons'
import PreviewRegistry from './PreviewRegistry'
import PreviewTemplate from './PreviewTemplate'
import css from './styles.scss'
import { useNavigate } from 'react-router-dom-v5-compat'

export interface RegistryProps {
  id: number
  title: string
  photoUrl: string
  role: string
  regItemsCount: number
  urlSlug: string
}

export interface TemplateProps {
  id: number
  photoUrl: string
  title: string
  type: string
  description: string
}

const RELATIVE_PATHS = {
  ROOT: '',
  PREVIEW_REGISTRY: 'registry',
  PREVIEW_TEMPLATE: 'starter',
  COPY_REGISTRY: 'copy-registry',
}

const PATHS = (() => {
  const temp = { ...RELATIVE_PATHS }
  let key: keyof typeof RELATIVE_PATHS
  for (key in temp) {
    temp[key] = `/templates/${temp[key]}`
  }

  return temp
})()

function ChooseTemplateFlow({ registry, user }: any) {
  const navigate = useNavigate()
  const tracker = useTracking()
  const { data } = useQuery({
    queryKey: ['registryTemplates'],
    queryFn: () => fetchTemplates(),
  })
  const templates = data?.templates ?? []

  // currentRegistry is either the registry object that gets added to redux from
  // the registry-create page or hydrated from the user object if the user
  // does a hard refresh on the page.
  const currentRegistry = registry?.registry ?? user?.currentRegistry

  const handleStartFromScratch = () => {
    tracker.trackEvent({
      event: track.registryTemplateTypeClicked,
      eventLocation: track.EventLocation.REGISTRY_STARTER_PAGE,
      registryTemplateType: track.RegistryTemplateType.BLANK,
    })
    // Redirect to new registry
    window.location.href = registryPath(currentRegistry.urlSlug, {
      confetti: true,
    })
  }

  const handleCopyExistingRegistry = () => {
    navigate(PATHS.COPY_REGISTRY)
  }

  const handlePreviewRegistry = (id: number) => {
    tracker.trackEvent({
      event: track.registryTemplateTypeClicked,
      eventLocation: track.EventLocation.REGISTRY_STARTER_PAGE,
      registryTemplateType: track.RegistryTemplateType.PRIOR_REG,
    })
    navigate(`${PATHS.PREVIEW_REGISTRY}/${id}`)
  }

  const handlePreviewTemplate = (id: number) => {
    tracker.trackEvent({
      event: track.registryTemplateTypeClicked,
      eventLocation: track.EventLocation.REGISTRY_STARTER_PAGE,
      registryTemplateType: track.RegistryTemplateType.REG_TEMPLATE,
    })
    navigate(`${PATHS.PREVIEW_TEMPLATE}/${id}`)
  }

  return (
    <Routes>
      <Route
        path={`${RELATIVE_PATHS.PREVIEW_REGISTRY}/:id`}
        element={<PreviewRegistry currentRegistry={currentRegistry} />}
      />

      <Route
        path={`${RELATIVE_PATHS.PREVIEW_TEMPLATE}/:id`}
        element={<PreviewTemplate currentRegistry={currentRegistry} />}
      />

      <Route
        path={RELATIVE_PATHS.COPY_REGISTRY}
        element={
          <Slide title="Which registry would you like to copy?">
            {user?.registries
              ?.filter(
                (registry: RegistryProps) =>
                  registry.urlSlug !== currentRegistry.urlSlug &&
                  registry.role === 'owner'
              )
              .map((registry: RegistryProps) => (
                <button
                  className="flex btn-unstyled text-left align-center mbm pbl"
                  key={registry.id}
                  style={{ width: '100%', maxWidth: 360 }}
                  type="button"
                  onClick={() => handlePreviewRegistry(registry.id)}
                  onKeyDown={() => handlePreviewRegistry(registry.id)}
                >
                  {registry.photoUrl ? (
                    <Image
                      className="circle-mask mrm"
                      height={60}
                      role="presentation"
                      src={registry.photoUrl}
                      width={60}
                    />
                  ) : (
                    <RegistryProfileIcon
                      className="mrm"
                      height={60}
                      width={60}
                    />
                  )}
                  <div className="flex col grow">
                    <div>{registry.title}</div>
                    <div className="h7">
                      {registry.regItemsCount}{' '}
                      {pluralize('item', registry.regItemsCount)}
                    </div>
                  </div>
                  <i className="fa fa-chevron-right pls text-body h7" />
                </button>
              ))}
          </Slide>
        }
      />

      <Route
        index
        element={
          <Slide title="Let's get started...">
            <ButtonGroup>
              <ButtonRect
                className={undefined}
                onClick={handleStartFromScratch}
              >
                <NewListIcon />
                <div className="h5 mtl mbn">Start from scratch</div>
              </ButtonRect>
              <ButtonRect
                className={undefined}
                onClick={handleCopyExistingRegistry}
              >
                <CopyListIcon />
                <div className="h5 mtl mbn">Copy your existing registry</div>
              </ButtonRect>
            </ButtonGroup>
            <div className="mvxl text-muted">Start from a Sample Registry</div>
            <div className={css.templatesGrid}>
              {templates.length > 0 &&
                templates.map((template: TemplateProps) => (
                  <button
                    className="flex btn-unstyled text-left"
                    key={template.id}
                    type="button"
                    onClick={() => handlePreviewTemplate(template.id)}
                  >
                    {template.photoUrl ? (
                      <Image
                        className="circle-mask mrm"
                        height={75}
                        role="presentation"
                        src={template.photoUrl}
                        width={75}
                      />
                    ) : (
                      <RegistryProfileIcon
                        className="mrm"
                        height={75}
                        width={75}
                      />
                    )}
                    <div>
                      <div className="h4 mvn pbs">{template.title}</div>
                      {template.description && (
                        <div className="text-muted mvn pbs h7">
                          {template.description}
                        </div>
                      )}
                      <LinkButton className="link-info h7 man pan text-bold">
                        Start from this sample registry{' '}
                        <i className="fa fa-chevron-right pls text-body h7" />
                      </LinkButton>
                    </div>
                  </button>
                ))}
            </div>
          </Slide>
        }
      />
    </Routes>
  )
}

const mapStateToProps = (state: any) => ({
  user: getUser(state),
  registry: getRegistry(state),
})

export default connect(mapStateToProps)(ChooseTemplateFlow)
